import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs } from "@expert/shared-types";

import { Flex, Text } from "@mantine/core";

import type { ExpertAssistMessage } from "../../shared-types";

import { useSendExpertAssistMessage } from "../../hooks";
import { useTimelineStore } from "../../state";
import classes from "./RetryButton.module.css";

interface RetryButtonProps {
    message: ExpertAssistMessage;
    logger: Logger;
    gaiaPayloadProps: GaiaPayloadConfigs;
}

function WarningIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
        >
            <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.25" />
            <path d="M7 4.5V7.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
            <circle cx="7" cy="9.5" r="0.625" fill="currentColor" />
        </svg>
    );
}

export function RetryButton({ message, logger, gaiaPayloadProps }: RetryButtonProps) {
    const sendMessage = useSendExpertAssistMessage(logger, gaiaPayloadProps);
    const { removeMessages, currentSessionId } = useTimelineStore();
    const retryPayload = message.metadata?.retryPayload;

    if (!retryPayload) return null;

    const handleRetry = () => {
        removeMessages(currentSessionId, [message.id]);
        sendMessage({
            text: retryPayload.text,
            type: retryPayload.type,
            source: "Retry",
            metadata: retryPayload.metadata,
        });
    };

    return (
        <Flex className={classes.container} align="center" gap={4}>
            <WarningIcon />
            <Text className={classes.message} size="sm">
                {"Couldn't get a response."}
            </Text>
            <button type="button" className={classes.tryAgain} onClick={handleRetry}>
                Resend
            </button>
        </Flex>
    );
}
